import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import { Link as RouterLink } from 'react-router-dom';

export interface EmptyStateProps {
  title: string;
  description?: string;
  icon?: ReactNode;
  /** Primary call to action. `to` renders a link, `onClick` a button. */
  action?: { label: string; to?: string; onClick?: () => void };
  secondaryAction?: { label: string; to?: string; onClick?: () => void };
  size?: 'small' | 'medium';
}

/**
 * Shown when a query succeeds with zero rows.
 *
 * Distinct from `ErrorState` on purpose: "no data yet" and "we could not load
 * the data" call for different words and different actions, and conflating them
 * is a common source of confused support tickets.
 */
export function EmptyState({
  title,
  description,
  icon,
  action,
  secondaryAction,
  size = 'medium',
}: EmptyStateProps): JSX.Element {
  const isSmall = size === 'small';

  const renderAction = (
    config: { label: string; to?: string; onClick?: () => void },
    variant: 'contained' | 'outlined',
  ): JSX.Element =>
    config.to ? (
      <Button component={RouterLink} to={config.to} variant={variant} size={isSmall ? 'small' : 'medium'}>
        {config.label}
      </Button>
    ) : (
      <Button onClick={config.onClick} variant={variant} size={isSmall ? 'small' : 'medium'}>
        {config.label}
      </Button>
    );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        px: 4,
        py: isSmall ? 6 : 10,
      }}
    >
      <Box
        aria-hidden="true"
        sx={(theme) => ({
          width: isSmall ? 44 : 56,
          height: isSmall ? 44 : 56,
          borderRadius: '50%',
          display: 'grid',
          placeItems: 'center',
          mb: 3,
          backgroundColor: theme.tones.neutral.surface,
          color: theme.tones.neutral.main,
          '& svg': { fontSize: isSmall ? 22 : 28 },
        })}
      >
        {icon ?? <InboxOutlinedIcon />}
      </Box>

      <Typography variant={isSmall ? 'h6' : 'h5'} component="p">
        {title}
      </Typography>

      {description && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, maxWidth: '48ch' }}>
          {description}
        </Typography>
      )}

      {(action || secondaryAction) && (
        <Stack direction="row" spacing={2} sx={{ mt: 4, flexWrap: 'wrap', justifyContent: 'center', gap: 2 }}>
          {action && renderAction(action, 'contained')}
          {secondaryAction && renderAction(secondaryAction, 'outlined')}
        </Stack>
      )}
    </Box>
  );
}
